import { createContext, useContext, useState, useCallback } from 'react';
import { getSuppliers, getCases, getVisas, invalidateAllCache } from '../utils/api';

const AppContext = createContext(null);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  // Global data state
  const [suppliers, setSuppliers] = useState([]);
  const [cases, setCases] = useState([]);
  const [visas, setVisas] = useState([]);
  const [loading, setLoading] = useState({
    suppliers: false,
    cases: false,
    visas: false
  });

  // Fetch suppliers
  const fetchSuppliers = useCallback(async (forceRefresh = false) => {
    setLoading(prev => ({ ...prev, suppliers: true }));
    try {
      const data = await getSuppliers(forceRefresh);
      setSuppliers(data);
      return data;
    } finally {
      setLoading(prev => ({ ...prev, suppliers: false }));
    }
  }, []);

  // Fetch cases (workers)
  const fetchCases = useCallback(async (forceRefresh = false) => {
    setLoading(prev => ({ ...prev, cases: true }));
    try {
      const data = await getCases(forceRefresh);
      setCases(data);
      return data;
    } finally {
      setLoading(prev => ({ ...prev, cases: false }));
    }
  }, []);

  // Fetch visas
  const fetchVisas = useCallback(async (forceRefresh = false) => {
    setLoading(prev => ({ ...prev, visas: true }));
    try {
      const data = await getVisas(forceRefresh);
      setVisas(data);
      return data;
    } finally {
      setLoading(prev => ({ ...prev, visas: false }));
    }
  }, []);

  // Refresh all data
  const refreshAll = useCallback(async () => {
    invalidateAllCache();
    await Promise.all([
      fetchSuppliers(true),
      fetchCases(true),
      fetchVisas(true)
    ]);
  }, [fetchSuppliers, fetchCases, fetchVisas]);

  // Calculate dashboard stats
  const getDashboardStats = useCallback(() => {
    const totalWorkers = cases.length;
    // Use 'status' field which contains: abroad, arrived, runaway, noarrival, completed, settled
    const abroad = cases.filter(c => c.status === 'abroad').length;
    const arrived = cases.filter(c => c.status === 'arrived').length;
    const noArrival = cases.filter(c => c.status === 'noarrival').length;
    const runaway = cases.filter(c => c.status === 'runaway').length;
    const settled = cases.filter(c => c.status === 'settled' || c.status === 'completed').length;
    
    // Financial calculations (excluding settled workers)
    const activeWorkers = cases.filter(c => c.status !== 'settled' && c.status !== 'completed');
    const totalCost = activeWorkers.reduce((sum, c) => sum + (c.totalCost || 0), 0);
    const totalPaid = activeWorkers.reduce((sum, c) => sum + (c.paidAmount || 0), 0);
    const totalDeductions = activeWorkers.reduce((sum, c) => sum + (c.deductionsUSD || 0), 0);
    const totalRemaining = activeWorkers.reduce((sum, c) => sum + (c.remainingAmount || 0), 0);

    return {
      totalWorkers,
      abroad,
      arrived,
      noArrival,
      runaway,
      settled,
      totalCost,
      totalPaid,
      totalDeductions,
      totalRemaining,
      totalSuppliers: suppliers.length,
      totalVisas: visas.length
    };
  }, [cases, suppliers.length, visas.length]);

  const value = {
    // Data
    suppliers,
    cases,
    visas,
    loading,
    // Fetchers
    fetchSuppliers,
    fetchCases,
    fetchVisas,
    refreshAll,
    // Computed
    getDashboardStats
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
